import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bell, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { WingedM, Tricolor } from "./Logo";
import { EASE, MaskLine } from "./Reveal";

const TICKER = ["Drop 01", "Kommt bald", "Born to Fight", "Mazaro", "Dark & White Kollektion"];

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetIso) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [targetIso]);
  if (!targetIso) return null;
  const diff = new Date(targetIso).getTime() - now;
  if (!Number.isFinite(diff) || diff <= 0) return { live: true };
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor(diff / 3600000) % 24,
    minutes: Math.floor(diff / 60000) % 60,
    seconds: Math.floor(diff / 1000) % 60,
  };
}

function CountdownTile({ value, label, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center border border-gold/30 bg-black/40 px-2 py-3 sm:px-5 sm:py-4 min-w-[64px] sm:min-w-[86px]">
      <span className="font-display font-black text-3xl sm:text-5xl leading-none text-white tabular-nums overflow-hidden">
        <motion.span
          key={value}
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="block"
        >
          {String(value).padStart(2, "0")}
        </motion.span>
      </span>
      <span className="font-mono text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-gold mt-2">{label}</span>
    </div>
  );
}

function TickerTrack({ hidden = false }) {
  return (
    <div aria-hidden={hidden} className="flex shrink-0 items-center">
      {TICKER.map((t, i) => (
        <span key={t} className="flex items-center">
          <span
            className={`mx-6 sm:mx-10 font-display font-black uppercase tracking-tight text-2xl sm:text-4xl whitespace-nowrap ${
              i % 2 ? "text-stroke-gold" : "text-white/80"
            }`}
          >
            {t}
          </span>
          <span className="w-2 h-2 bg-gold rotate-45 shrink-0" />
        </span>
      ))}
    </div>
  );
}

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [dropDate, setDropDate] = useState(null);
  const countdown = useCountdown(dropDate);

  useEffect(() => {
    api
      .get("/config")
      .then(({ data }) => setDropDate(data.drop_date))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vip/join", { email: email.trim() });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="coming-soon-section" className="relative min-h-screen bg-ink text-white flex flex-col overflow-hidden">
      <div className="grain" aria-hidden="true" />
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-gold/10 blur-[140px]" />

      <main className="flex-1 flex items-center justify-center px-4 pt-20 pb-10 relative z-10">
        <div className="text-center max-w-2xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="flex justify-center"
          >
            <WingedM size={96} />
          </motion.div>

          <MaskLine delay={0.3}>
            <div className="flex flex-col items-center gap-3 mt-6">
              <p className="font-mono text-[11px] sm:text-xs tracking-[0.35em] uppercase text-gold">
                Mazaro — Born to Fight
              </p>
              <Tricolor className="w-16" />
            </div>
          </MaskLine>

          <h1 className="mt-8 font-display font-black uppercase tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-8xl">
            <MaskLine delay={0.45}>Drop 01</MaskLine>
            <MaskLine delay={0.58}>
              <span className="text-stroke-gold">kommt bald</span>
            </MaskLine>
          </h1>

          <MaskLine delay={0.72}>
            <p className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed max-w-lg mx-auto">
              Die Dark & White Kollektion — Fight-Grade Handschuhe und Bandagen, gebaut im Gym, getestet im Ring. Der
              Shop öffnet in Kürze: Trag dich ein und erfahre als Erster, wenn der Drop live geht.
            </p>
          </MaskLine>

          {countdown && !countdown.live && (
            <MaskLine delay={0.8}>
              <div className="mt-9" data-testid="countdown-section">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">Drop startet in</p>
                <div className="flex justify-center gap-2 sm:gap-3">
                  <CountdownTile value={countdown.days} label="Tage" testid="countdown-days" />
                  <CountdownTile value={countdown.hours} label="Std" testid="countdown-hours" />
                  <CountdownTile value={countdown.minutes} label="Min" testid="countdown-minutes" />
                  <CountdownTile value={countdown.seconds} label="Sek" testid="countdown-seconds" />
                </div>
              </div>
            </MaskLine>
          )}

          <MaskLine delay={0.85}>
            {done ? (
              <div
                data-testid="coming-soon-success"
                className="mt-10 max-w-md mx-auto border border-gold/40 bg-gold/10 px-6 py-6"
              >
                <span className="inline-flex w-10 h-10 items-center justify-center bg-gold text-ink mb-3">
                  <Check size={18} />
                </span>
                <p className="font-display font-bold uppercase tracking-tight text-xl">Du bist dabei</p>
                <p className="text-slate-300 text-sm mt-2">
                  Wir benachrichtigen dich unter <span className="text-gold">{email}</span>, sobald der Drop live geht.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="coming-soon-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.de"
                  data-testid="coming-soon-email-input"
                  className="flex-1 bg-black/40 border border-white/15 px-5 py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold"
                />
                <button type="submit" disabled={busy} data-testid="coming-soon-submit-button" className="btn-gold disabled:opacity-60">
                  Benachrichtigen
                  <Bell size={15} />
                </button>
              </form>
            )}
          </MaskLine>

          <MaskLine delay={1.05}>
            <p className="mt-5 font-mono text-[10px] tracking-[0.25em] uppercase text-white/30">Kein Spam. Nur Drops.</p>
          </MaskLine>
        </div>
      </main>

      <section className="relative z-10 border-t border-white/10 bg-panel px-4 sm:px-8 py-14" data-testid="seo-content">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12">
            <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl text-white mb-3">
              Die neue Definition von Boxing
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              MAZARO ist die deutsche Boxmarke für Fight-Grade Ausrüstung. Unser Anspruch: kompromisslose Präzision und
              athletischer Luxus — jede Ausrüstung entsteht aus dem Training heraus und wird an der Tasche getestet,
              nicht am Schreibtisch. Gebaut im Gym, getestet im Ring: Das gilt für jede Naht, jeden Schaumkern und jede
              Verstellung. Ob Sparring, Pad-Arbeit oder Titelkampf — MAZARO begleitet Boxerinnen und Boxer vom ersten
              Rundenschlag an mit Ausrüstung, die hält, was die Marke verspricht.
            </p>
          </div>

          <div className="mb-12">
            <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl text-white mb-3">
              Dark &amp; White Kollektion — Drop 01
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Drop 01 umfasst zwei Linien: die <strong className="text-white">Dark Kollektion</strong> mit der APEX PRO 16
              OZ in Obsidian Gold (schwarzes Fight-Grade Leder mit goldenen Cut-Linien) und den SENTINEL Bandagen in
              Schwarz/Gold — sowie die <strong className="text-white">White Kollektion</strong> mit der APEX PRO 16 OZ in
              Bianco Gold und den SENTINEL Bandagen in Weiß/Gold. Jeder Profi-Boxhandschuh vereint den dreilagigen
              3-Layer Impact Foam mit dem 360° Wrist Lock für volle Handgelenkskontrolle; die 4,5-Meter-Bandagen aus
              Halbspanne tragen das goldene Mazaro-Siegel und sind maschinenwaschbar.
            </p>
          </div>

          <div className="mb-12">
            <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl text-white mb-3">
              Technologie, die trägt
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Vier Technologien stecken in jedem Produkt: 3-Layer Impact Foam verteilt Schlagenergie kontrolliert, der
              360° Wrist Lock umschließt das Handgelenk vollständig für Stabilität ohne Steifheit, das
              Mikrofaser-Leder ist abriebfest und geschmeidig ab der ersten Runde, und Heat-Flow Ventilation hält die
              Hitze draußen und die Konzentration drin.
            </p>
          </div>

          <div>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl text-white mb-6">
              Häufige Fragen
            </h2>
            <div className="space-y-6">
              {[
                ["Wann startet MAZARO Drop 01?", "Drop 01 startet am 1. Dezember 2026 um 18:00 Uhr. Bis dahin kannst du dich auf dieser Seite für die Benachrichtigungsliste anmelden."],
                ["Welche Produkte enthält Drop 01?", "Die APEX PRO 16 OZ Boxhandschuhe in Obsidian Gold und Bianco Gold sowie die SENTINEL Bandagen (4,5 m Halbspanne) in Schwarz/Gold und Weiß/Gold."],
                ["Für welches Training ist welches Handschuhgewicht gedacht?", "12 oz eignen sich für Pad- und Sackarbeit, 14 oz für Technik und leichtes Sparring und 16 oz für Sparring und Fight."],
                ["Wie erfahre ich, wenn der Shop öffnet?", "Trag deine E-Mail-Adresse auf dieser Seite ein — wir benachrichtigen dich, sobald Drop 01 live geht."],
              ].map(([q, a]) => (
                <div key={q} className="border-l-2 border-gold/40 pl-4" data-testid="seo-faq-item">
                  <h3 className="font-display font-bold uppercase tracking-tight text-lg text-gold">{q}</h3>
                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed mt-1.5">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div data-testid="coming-soon-ticker" className="relative border-t border-white/10 bg-panel py-4 sm:py-5 overflow-hidden">
        <div className="flex w-max animate-marquee">
          <TickerTrack />
          <TickerTrack hidden />
        </div>
      </div>

      <footer className="border-t border-white/10 bg-panel relative z-10">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-16 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/30">
            © {new Date().getFullYear()} Mazaro Boxing
          </p>
          <nav className="flex flex-wrap justify-center gap-x-4 gap-y-2" data-testid="splash-legal-links">
            {[
              ["/impressum", "Impressum"],
              ["/datenschutz", "Datenschutz"],
              ["/agb", "AGB"],
              ["/widerruf", "Widerruf"],
              ["/kontakt", "Kontakt"],
            ].map(([href, label]) => (
              <a key={href} href={href} data-testid={`splash-legal-${label.toLowerCase()}`} className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-gold transition-colors">
                {label}
              </a>
            ))}
          </nav>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-gold/70">Status: Geschlossen — Drop 01 Bald</p>
        </div>
      </footer>
    </div>
  );
}
